import { useState } from 'react';
import type { OccurrenceWithTimeSpent } from '../../../models/occurrence';
import { getStepLabelId } from '../trackingUtils';
import type { StageSummary } from '../types';
import BoardColumnCards from './BoardColumnCards';
import StageHeat from './StageHeat';
import useMtText from './useMtText';
import './TrackingBoard.scss';

interface ITrackingBoardProps {
    items: OccurrenceWithTimeSpent[];
    stages: StageSummary[];
    plateByChassis: Map<string, string>;
    activeStage: string | null;
    onStageClick: (step: string) => void;
    onOpen: (occurrenceId: string) => void;
    emptyMessage: string;
}

const TrackingBoard = ({
    items,
    stages,
    plateByChassis,
    activeStage,
    onStageClick,
    onOpen,
    emptyMessage,
}: ITrackingBoardProps) => {
    const { t, msg, formatNumber, formatDuration } = useMtText();
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

    const toggleColumn = (stepKey: string) =>
        setCollapsed(prev => {
            const next = new Set(prev);
            if (next.has(stepKey)) {
                next.delete(stepKey);
            } else {
                next.add(stepKey);
            }
            return next;
        });

    if (!items.length) {
        return <div className='mt-empty'>{emptyMessage}</div>;
    }

    return (
        <div className='mt-boardwrap'>
            <div className='mt-board'>
                {stages.map(stage => {
                    const name = msg(getStepLabelId(stage.step));
                    const columnItems = items.filter(item => item.currentStep === stage.step);

                    if (collapsed.has(stage.stepKey)) {
                        return (
                            <button
                                type='button'
                                key={stage.stepKey}
                                className='mt-board__col mt-board__col--collapsed'
                                aria-pressed={activeStage === stage.step}
                                aria-label={`${t('board.expand')}: ${name}`}
                                onClick={() => toggleColumn(stage.stepKey)}
                            >
                                <span className='rioglyph rioglyph-chevron-right' />
                                <span className='mt-board__vname'>
                                    {name} <span className='mt-num'>({formatNumber(columnItems.length)})</span>
                                </span>
                            </button>
                        );
                    }

                    return (
                        <div key={stage.stepKey} className='mt-board__col'>
                            <div className='mt-board__head' data-active={activeStage === stage.step}>
                                <button
                                    type='button'
                                    className='mt-board__stage'
                                    aria-pressed={activeStage === stage.step}
                                    title={
                                        stage.estimateMs
                                            ? t('summary.estimated', { time: formatDuration(stage.estimateMs) })
                                            : undefined
                                    }
                                    onClick={() => onStageClick(stage.step)}
                                >
                                    <span className='mt-stage__name'>{name}</span>
                                    <span className='mt-stage__time mt-num'>{formatDuration(stage.averageMs)}</span>
                                    <span className='mt-stage__count mt-num'>
                                        {t('summary.vehicles', { count: formatNumber(stage.currentCount) })}
                                    </span>
                                    <StageHeat stage={stage} />
                                </button>
                                <button
                                    type='button'
                                    className='mt-board__toggle'
                                    aria-label={`${t('board.collapse')}: ${name}`}
                                    title={t('board.collapse')}
                                    onClick={() => toggleColumn(stage.stepKey)}
                                >
                                    <span className='rioglyph rioglyph-chevron-left' />
                                </button>
                            </div>
                            <BoardColumnCards
                                items={columnItems}
                                estimateMs={stage.estimateMs}
                                plateByChassis={plateByChassis}
                                onOpen={onOpen}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default TrackingBoard;
